import { format, isToday, isTomorrow, isYesterday, startOfMonth, startOfWeek, startOfYear, subYears } from 'date-fns';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../../design-system/button/button';
import { Calendar } from '../../../../design-system/calendar/calendar';
import { Inline } from '../../../../design-system/inline/inline';
import { Popover } from '../../../../design-system/popover/popover';
import { Stack } from '../../../../design-system/stack/stack';
import { cn } from '../../../../utils/cn';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';

const DATE_LABEL_FORMAT = 'dd MMM yyyy';

const shortcutButtonVariants = ['ONE_YEAR_AGO', 'START_OF_YEAR', 'START_OF_MONTH', 'START_OF_WEEK'] as const;
type DateShortcutVariant = (typeof shortcutButtonVariants)[number];

type DatePickerProps = {
  value: Date | undefined;
  onTogglePopover?: (open: boolean) => void;
  onChangeValue: (date: Date) => void;
  className?: string;
};

export const DatePicker = ({ value, onTogglePopover, onChangeValue, className }: DatePickerProps) => {
  const TODAY = new Date();
  const ONE_YEAR_AGO = subYears(TODAY, 1);

  const [isOpen, setIsOpen] = useState(false);

  const displayDateLabel = getDisplayDateLabel(value);

  const handleSelect = (date: Date | undefined) => {
    if (!date) {
      return;
    }

    onChangeValue(date);
    setIsOpen(false);
  };

  const handleShortcutSelect = (variant: DateShortcutVariant) => {
    switch (variant) {
      case 'ONE_YEAR_AGO':
        handleSelect(ONE_YEAR_AGO);

        return;
      case 'START_OF_YEAR':
        handleSelect(startOfYear(TODAY));

        return;
      case 'START_OF_MONTH':
        handleSelect(startOfMonth(TODAY));

        return;
      case 'START_OF_WEEK':
        handleSelect(
          startOfWeek(TODAY, {
            weekStartsOn: 1,
          }),
        );

        return;
      default:
        exhaustiveCheck(variant);
    }
  };

  return (
    <Popover
      open={isOpen}
      triggerElement={
        <Button
          variant="outline"
          iconRight={isOpen ? <ChevronUp /> : <ChevronDown className="h-5 w-5" />}
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            'w-full justify-between rounded-[10px] px-4 py-3 text-sm focus:outline-none focus:ring-ring focus:ring-offset-2 focus-visible:ring-2',
            {
              'text-black': !!value,
              'text-seapoint-gray-text': !value,
            },
            className,
          )}
        >
          {displayDateLabel}
        </Button>
      }
      content={
        <Inline space={0} align="stretch">
          <Calendar
            mode="single"
            numberOfMonths={1}
            defaultMonth={startOfMonth(value ?? TODAY)}
            startMonth={ONE_YEAR_AGO}
            navLayout="around"
            selected={value}
            onSelect={handleSelect}
            className="rounded-bl-xl rounded-tl-xl bg-white p-5"
            classNames={{
              selected: 'text-seapoint-yellow bg-black rounded-full font-normal',
              button_previous: 'inline-flex top-6 ml-5 absolute start-0',
              button_next: 'inline-flex top-6 mr-5 absolute end-0',
            }}
            showOutsideDays
          />

          <Stack
            space={2}
            justify="stretch"
            align="center"
            className="rounded-br-xl rounded-tr-xl bg-seapoint-gray-medium p-5"
          >
            {shortcutButtonVariants.map((option) => (
              <DateShortcutButton key={option} variant={option} onSelectShortcut={handleShortcutSelect} />
            ))}
          </Stack>
        </Inline>
      }
      className="bg-white"
      onOpenChange={(open) => {
        setIsOpen(open);
        onTogglePopover?.(open);
      }}
    />
  );
};

const getDisplayDateLabel = (date: Date | undefined) => {
  if (!date) {
    return 'Select date';
  }

  if (isYesterday(date)) {
    return 'Yesterday';
  }

  if (isToday(date)) {
    return 'Today';
  }

  if (isTomorrow(date)) {
    return 'Tomorrow';
  }

  return format(date, DATE_LABEL_FORMAT);
};

type DateShortcutButtonProps = {
  variant: DateShortcutVariant;
  onSelectShortcut: (variant: DateShortcutVariant) => void;
};

const DateShortcutButton = ({ variant, onSelectShortcut }: DateShortcutButtonProps) => {
  const label = getDateShortcutLabel(variant);

  return (
    <Button
      variant="ghost"
      onClick={() => onSelectShortcut(variant)}
      className="w-full text-xs font-normal leading-5 tracking-tight text-seapoint-gray hover:bg-white"
    >
      {label}
    </Button>
  );
};

const getDateShortcutLabel = (variant: DateShortcutVariant) => {
  switch (variant) {
    case 'ONE_YEAR_AGO':
      return 'One year';
    case 'START_OF_YEAR':
      return 'Start of Year';
    case 'START_OF_MONTH':
      return 'Start of Month';
    case 'START_OF_WEEK':
      return 'Start of Week';
    default:
      return exhaustiveCheck(variant);
  }
};
